import type {
  QuestionCustomizationsQuestion,
  QuestionUsage,
} from '../../services/questionCustomizations';

export type PartitionedQuestionRow = {
  question: QuestionCustomizationsQuestion;
  isDefault: boolean;
  isSelected: boolean;
  displayLabel: string;
};

export type PartitionThreeTiersOptions = {
  questions: QuestionCustomizationsQuestion[];
  defaultQids: string[];
  additionQids: string[];
  usage: QuestionUsage;
  searchQuery?: string;
  getDisplayLabel?: (question: QuestionCustomizationsQuestion) => string;
};

/**
 * Determine if a question belongs to OASIS assessments.
 *
 * A question is considered OASIS if it appears in at least one form
 * belonging to the 'oasis-assessments' taxonomy.
 */
export function isOasisQuestion(
  question: QuestionCustomizationsQuestion,
  usage: QuestionUsage
): boolean {
  const sources = usage.get(question.qid);
  if (!sources || sources.length === 0) return false;
  return sources.some(source => source.taxonomyKey === 'oasis-assessments');
}

function toRow(
  question: QuestionCustomizationsQuestion,
  isDefault: boolean,
  isSelected: boolean,
  getDisplayLabel: (q: QuestionCustomizationsQuestion) => string
): PartitionedQuestionRow {
  return {
    question,
    isDefault,
    isSelected,
    displayLabel: getDisplayLabel(question),
  };
}

/**
 * Partition questions into the unified 3-tier structure:
 * Tier 1: Defaults (Always at top, non-removable, neutral badge)
 * Tier 2: Selected additions (Middle tier, red border, remove button)
 * Tier 3: Unselected OASIS questions (Bottom tier, alphabetical, + Add button)
 *
 * Applies case-insensitive search filtering across all tiers while preserving tier order.
 */
export function partitionThreeTiers({
  questions,
  defaultQids,
  additionQids,
  usage,
  searchQuery = '',
  getDisplayLabel = q => q.description,
}: PartitionThreeTiersOptions): PartitionedQuestionRow[] {
  const defaultQidSet = new Set(defaultQids);
  const additionQidSet = new Set(additionQids);
  const questionsByQid = new Map(questions.map(q => [q.qid, q]));

  // Tier 1: Defaults (Always top)
  const tier1Defaults: PartitionedQuestionRow[] = defaultQids
    .map(qid => questionsByQid.get(qid))
    .filter((q): q is QuestionCustomizationsQuestion => Boolean(q))
    .map(question => toRow(question, true, false, getDisplayLabel));

  // Tier 2: Selected Additions (Middle)
  // Preserves additionQids order. Excludes any accidental defaults.
  const tier2Selected: PartitionedQuestionRow[] = additionQids
    .filter(qid => !defaultQidSet.has(qid))
    .map(qid => {
      const existing = questionsByQid.get(qid);
      const question: QuestionCustomizationsQuestion = existing ?? {
        qid,
        description: 'unavailable question',
        question: '',
        available: false,
      };
      return toRow(question, false, true, getDisplayLabel);
    });

  // Tier 3: Unselected OASIS questions (Bottom)
  // Available OASIS catalog questions not in defaults or additions.
  // Sorted alphabetically by display label.
  const unselectedQuestions = questions.filter(question => {
    if (defaultQidSet.has(question.qid)) return false;
    if (additionQidSet.has(question.qid)) return false;
    return isOasisQuestion(question, usage);
  });

  unselectedQuestions.sort((a, b) =>
    getDisplayLabel(a).localeCompare(getDisplayLabel(b))
  );

  const tier3Unselected: PartitionedQuestionRow[] = unselectedQuestions.map(
    question => toRow(question, false, false, getDisplayLabel)
  );

  // Apply search query across tiers if present
  const query = searchQuery.trim().toLowerCase();
  const matches = (row: PartitionedQuestionRow) => {
    if (!query) return true;
    return (
      row.question.qid.toLowerCase().includes(query) ||
      row.displayLabel.toLowerCase().includes(query) ||
      (row.question.question || '').toLowerCase().includes(query) ||
      (row.question.description || '').toLowerCase().includes(query)
    );
  };

  return [...tier1Defaults, ...tier2Selected, ...tier3Unselected].filter(
    matches
  );
}
